import { monotonicFactory } from 'ulid';

// Monotonic so ids minted within the same millisecond still sort in
// insertion order — getActiveStrokes relies on ulid order for z-order.
const ulid = monotonicFactory();
// Exported so other modules (e.g. Task 10 fragment minting) share the same
// monotonic ordering instead of creating a second, independently-seeded factory.
export const newId = ulid;
import type { CoteDatabase } from './db';
import type { Image, Notebook, Page, PageKind, Settings, StoredStroke, TemplateId, TextBox } from './types';
import type { Rect, Stroke } from '../ink/stroke';
import { bytesToB64, type Mutation, type StrokeMeta } from '../sync/protocol';

const DEFAULT_SETTINGS: Settings = {
  singleFingerDraws: false,
  penSeen: false,
  lastColor: 'ink-charcoal',
  lastWidth: 3,
  lastEraserRadius: 12,
  shapeSnap: true,
};

/**
 * Platform-aware default (spec intent: draw with one finger on mouse-primary
 * devices, pan on touch-first devices like iPad). Only used when no value has
 * been stored. Returns false where matchMedia is unavailable (node tests).
 */
function defaultSingleFingerDraws(): boolean {
  if (typeof matchMedia === 'undefined') return false;
  try {
    return matchMedia('(pointer: fine)').matches && !matchMedia('(any-pointer: coarse)').matches;
  } catch {
    return false;
  }
}

export interface NewStroke {
  pageId: string;
  tool: 'pen' | 'highlighter';
  color: string;
  baseWidth: number;
  points: Float32Array;
  bbox: Rect;
}

/**
 * Server-resolved record shapes for applyRemote* — same as the local records
 * but deletedAt may be an explicit null (SQL NULL) instead of absent.
 */
export type RemoteNotebook = Omit<Notebook, 'deletedAt'> & { deletedAt?: number | null };
export type RemotePage = Omit<Page, 'deletedAt'> & { deletedAt?: number | null };
export type RemoteStroke = Omit<StoredStroke, 'deletedAt'> & { deletedAt?: number | null };
export type RemoteTextBox = Omit<TextBox, 'deletedAt'> & { deletedAt?: number | null };
export type RemoteImage = Omit<Image, 'deletedAt'> & { deletedAt?: number | null };

/** A purge row from the changes feed (terminal hard-delete, no LWW). */
export interface RemotePurge {
  target: 'notebook' | 'page';
  id: string;
}

/**
 * Stamp a record's updatedAt strictly after its previous value, even within
 * the same millisecond. Server LWW keeps an incoming row only when its
 * updatedAt is STRICTLY newer: an equal stamp would be rejected there, and
 * the pull echo of the stale server row would then regress local state
 * (e.g. roll a page's rev backwards and poison the rev-keyed thumbnail cache).
 */
function bumpUpdatedAt(rec: { updatedAt: number }): number {
  rec.updatedAt = Math.max(Date.now(), rec.updatedAt + 1);
  return rec.updatedAt;
}

// ---- outbox mutation builders -------------------------------------------

function notebookMutation(nb: Notebook): Mutation {
  return { kind: 'notebook', id: nb.id, data: nb, updatedAt: nb.updatedAt, deletedAt: nb.deletedAt ?? null };
}

function pageMutation(p: Page): Mutation {
  return { kind: 'page', id: p.id, data: p, updatedAt: p.updatedAt, deletedAt: p.deletedAt ?? null, rev: p.rev };
}

function textboxMutation(b: TextBox): Mutation {
  return { kind: 'textbox', id: b.id, pageId: b.pageId, data: b, updatedAt: b.updatedAt, deletedAt: b.deletedAt ?? null };
}

function imageMutation(img: Image): Mutation {
  return { kind: 'image', id: img.id, pageId: img.pageId, data: img, updatedAt: img.updatedAt, deletedAt: img.deletedAt ?? null };
}

function strokeMeta(s: StoredStroke): StrokeMeta {
  return { tool: s.tool, color: s.color, baseWidth: s.baseWidth, bbox: s.bbox, createdAt: s.createdAt };
}

/**
 * Full stroke insert (points included). `updatedAt` is only stamped for live
 * user intent (putStroke): it lets an undo re-insert clear a server-side
 * tombstone via LWW. Bulk re-journaling (enqueueFullExport) omits it so an
 * export can never resurrect a stroke another device erased.
 */
function strokeInsertMutation(s: StoredStroke, updatedAt?: number): Mutation {
  const m: Mutation = {
    kind: 'stroke', id: s.id, pageId: s.pageId, meta: strokeMeta(s),
    pointsB64: bytesToB64(new Uint8Array(s.points)),
    createdAt: s.createdAt, deletedAt: s.deletedAt ?? null,
  };
  if (updatedAt !== undefined) m.updatedAt = updatedAt;
  return m;
}

/** File-cache byte budget (M4 spec §1): 200MB, LRU-by-size. */
const FILE_CACHE_BYTES = 200 * 1024 * 1024;

export class Repo {
  /**
   * Invoked (fire-and-forget) after every transaction that wrote outbox
   * entries. The sync engine sets this to schedule a debounced push.
   */
  onMutation?: () => void;

  private readonly fileCacheBytes: number;
  /** Single-flight fetches by fileId (getFile): concurrent misses share one fetcher call. */
  private readonly fileFetches = new Map<string, Promise<ArrayBuffer>>();

  constructor(private db: CoteDatabase, opts: { fileCacheBytes?: number } = {}) {
    this.fileCacheBytes = opts.fileCacheBytes ?? FILE_CACHE_BYTES;
  }

  private notifyMutation(): void {
    try {
      this.onMutation?.();
    } catch {
      // The hook must never break the persistence path.
    }
  }

  // ---- notebooks ----
  async createNotebook(opts: { title: string; cover: number; defaultTemplate: TemplateId; defaultKind: PageKind; folderPath?: string }): Promise<{ notebook: Notebook; page: Page }> {
    const now = Date.now();
    const page: Page = {
      id: ulid(), notebookId: '', kind: opts.defaultKind, templateId: opts.defaultTemplate,
      rev: 0, createdAt: now, updatedAt: now,
    };
    const notebook: Notebook = {
      id: ulid(), title: opts.title, cover: opts.cover, folderPath: opts.folderPath ?? '',
      defaultTemplate: opts.defaultTemplate, defaultKind: opts.defaultKind,
      pageOrder: [page.id], createdAt: now, updatedAt: now,
    };
    page.notebookId = notebook.id;
    const tx = this.db.transaction(['notebooks', 'pages', 'outbox'], 'readwrite');
    await tx.objectStore('notebooks').put(notebook);
    await tx.objectStore('pages').put(page);
    const outbox = tx.objectStore('outbox');
    await outbox.add({ mutation: notebookMutation(notebook), enqueuedAt: now });
    await outbox.add({ mutation: pageMutation(page), enqueuedAt: now });
    await tx.done;
    this.notifyMutation();
    return { notebook, page };
  }

  async getNotebook(id: string): Promise<Notebook | undefined> { return this.db.get('notebooks', id); }

  async listNotebooks(): Promise<Notebook[]> {
    const all = await this.db.getAll('notebooks');
    return all.filter((n) => !n.deletedAt).sort((a, b) => b.updatedAt - a.updatedAt);
  }

  async updateNotebook(nb: Notebook): Promise<void> {
    bumpUpdatedAt(nb);
    const tx = this.db.transaction(['notebooks', 'outbox'], 'readwrite');
    await tx.objectStore('notebooks').put(nb);
    await tx.objectStore('outbox').add({ mutation: notebookMutation(nb), enqueuedAt: nb.updatedAt });
    await tx.done;
    this.notifyMutation();
  }

  async softDeleteNotebook(id: string): Promise<void> {
    await this.setNotebookDeleted(id, Date.now());
  }

  async restoreNotebook(id: string): Promise<void> {
    await this.setNotebookDeleted(id, null);
  }

  /** deletedAt also bumps updatedAt: server LWW is updatedAt-based, so a delete/restore must be "newer" to propagate. */
  private async setNotebookDeleted(id: string, deletedAt: number | null): Promise<void> {
    const tx = this.db.transaction(['notebooks', 'outbox'], 'readwrite');
    const nb = await tx.objectStore('notebooks').get(id);
    if (!nb) { await tx.done; return; }
    if (deletedAt === null) delete nb.deletedAt; else nb.deletedAt = deletedAt;
    const now = bumpUpdatedAt(nb);
    await tx.objectStore('notebooks').put(nb);
    await tx.objectStore('outbox').add({ mutation: notebookMutation(nb), enqueuedAt: now });
    await tx.done;
    this.notifyMutation();
  }

  // ---- pages ----
  async createPage(notebookId: string, opts: { kind: PageKind; templateId: TemplateId; afterPageId?: string; pdfRef?: Page['pdfRef']; size?: Page['size'] }): Promise<Page> {
    // Read the notebook INSIDE the readwrite tx: a read outside would race
    // concurrent writers (TOCTOU) and journal possibly-stale notebook data.
    const tx = this.db.transaction(['notebooks', 'pages', 'outbox'], 'readwrite');
    const nb = await tx.objectStore('notebooks').get(notebookId);
    if (!nb) {
      await tx.done;
      throw new Error(`notebook ${notebookId} not found`);
    }
    const now = Date.now();
    const page: Page = { id: ulid(), notebookId, kind: opts.kind, templateId: opts.templateId, rev: 0, createdAt: now, updatedAt: now };
    // M4 PDF import: pdfRef/size ride along opaquely (sync data JSON carries them).
    if (opts.pdfRef) page.pdfRef = { ...opts.pdfRef };
    if (opts.size) page.size = { ...opts.size };
    const i = opts.afterPageId ? nb.pageOrder.indexOf(opts.afterPageId) : -1;
    const at = i === -1 ? nb.pageOrder.length : i + 1; // unknown/stale afterPageId → append
    nb.pageOrder.splice(at, 0, page.id);
    bumpUpdatedAt(nb);
    await tx.objectStore('pages').put(page);
    await tx.objectStore('notebooks').put(nb);
    const outbox = tx.objectStore('outbox');
    await outbox.add({ mutation: notebookMutation(nb), enqueuedAt: now });
    await outbox.add({ mutation: pageMutation(page), enqueuedAt: now });
    await tx.done;
    this.notifyMutation();
    return page;
  }

  async getPage(id: string): Promise<Page | undefined> { return this.db.get('pages', id); }

  async softDeletePage(id: string): Promise<void> {
    await this.setPageDeleted(id, Date.now());
  }

  async restorePage(id: string): Promise<void> {
    await this.setPageDeleted(id, null);
  }

  /** deletedAt also bumps updatedAt — same LWW reasoning as setNotebookDeleted. */
  private async setPageDeleted(id: string, deletedAt: number | null): Promise<void> {
    const tx = this.db.transaction(['pages', 'outbox'], 'readwrite');
    const p = await tx.objectStore('pages').get(id);
    if (!p) { await tx.done; return; }
    if (deletedAt === null) delete p.deletedAt; else p.deletedAt = deletedAt;
    const now = bumpUpdatedAt(p);
    await tx.objectStore('pages').put(p);
    await tx.objectStore('outbox').add({ mutation: pageMutation(p), enqueuedAt: now });
    await tx.done;
    this.notifyMutation();
  }

  async savePageViewport(id: string, viewport: { x: number; y: number; scale: number }): Promise<void> {
    const tx = this.db.transaction(['pages', 'outbox'], 'readwrite');
    const p = await tx.objectStore('pages').get(id);
    if (!p) { await tx.done; return; }
    p.viewport = viewport;
    const now = bumpUpdatedAt(p);
    await tx.objectStore('pages').put(p);
    await tx.objectStore('outbox').add({ mutation: pageMutation(p), enqueuedAt: now });
    await tx.done;
    this.notifyMutation();
  }

  // ---- strokes ----
  async addStroke(s: NewStroke): Promise<Stroke> {
    const stroke: Stroke = { id: ulid(), createdAt: Date.now(), ...s };
    await this.putStroke(stroke);
    return stroke;
  }

  /** Insert a fully-formed stroke (used by eraser splits & undo). Bumps page rev; journals stroke + page. */
  async putStroke(stroke: Stroke): Promise<void> {
    const stored: StoredStroke = { ...stroke, points: f32ToBuffer(stroke.points) };
    const now = Date.now();
    const tx = this.db.transaction(['strokes', 'pages', 'outbox'], 'readwrite');
    await tx.objectStore('strokes').put(stored);
    const outbox = tx.objectStore('outbox');
    await outbox.add({ mutation: strokeInsertMutation(stored, now), enqueuedAt: now });
    const page = await tx.objectStore('pages').get(stroke.pageId);
    if (page) {
      page.rev += 1;
      bumpUpdatedAt(page);
      await tx.objectStore('pages').put(page);
      await outbox.add({ mutation: pageMutation(page), enqueuedAt: now });
    }
    await tx.done;
    this.notifyMutation();
  }

  async getActiveStrokes(pageId: string): Promise<Stroke[]> {
    const stored = await this.db.getAllFromIndex('strokes', 'byPage', pageId);
    return stored
      .filter((s) => !s.deletedAt)
      .sort((a, b) => (a.id < b.id ? -1 : 1)) // ulid = chronological
      .map((s) => ({ ...s, points: new Float32Array(s.points) }));
  }

  async setStrokeDeleted(id: string, deletedAt: number | null): Promise<void> {
    const now = Date.now();
    const tx = this.db.transaction(['strokes', 'pages', 'outbox'], 'readwrite');
    const s = await tx.objectStore('strokes').get(id);
    if (!s) { await tx.done; return; }
    const priorTombstone = s.deletedAt;
    if (deletedAt === null) delete s.deletedAt; else s.deletedAt = deletedAt;
    await tx.objectStore('strokes').put(s);
    const outbox = tx.objectStore('outbox');
    // Deletion-state-only mutation: no pointsB64 (points are immutable and
    // already on the server). updatedAt is required for tombstone *clears* so
    // the server can LWW-compare the clear against a stored tombstone — it
    // must be STRICTLY after that tombstone even within the same millisecond,
    // or the clear loses server LWW and the echo re-tombstones the stroke.
    const clearStamp = deletedAt === null ? Math.max(now, (priorTombstone ?? 0) + 1) : now;
    await outbox.add({
      mutation: {
        kind: 'stroke', id: s.id, pageId: s.pageId, meta: strokeMeta(s),
        createdAt: s.createdAt, deletedAt, updatedAt: clearStamp,
      },
      enqueuedAt: now,
    });
    const page = await tx.objectStore('pages').get(s.pageId);
    if (page) {
      page.rev += 1;
      bumpUpdatedAt(page);
      await tx.objectStore('pages').put(page);
      await outbox.add({ mutation: pageMutation(page), enqueuedAt: now });
    }
    await tx.done;
    this.notifyMutation();
  }

  /**
   * All strokes of a page INCLUDING tombstoned ones, in insertion order —
   * version history (spec §3) computes as-of-T visibility from
   * createdAt/deletedAt pairs, so tombstones must be visible to it.
   */
  async getAllStrokes(pageId: string): Promise<Stroke[]> {
    const stored = await this.db.getAllFromIndex('strokes', 'byPage', pageId);
    return stored
      .sort((a, b) => (a.id < b.id ? -1 : 1)) // ulid = chronological
      .map((s) => ({ ...s, points: new Float32Array(s.points) }));
  }

  // ---- text boxes (M3b spec §1) ---------------------------------------------

  /**
   * Upsert a text box (create, edit, move, restyle). Stamps updatedAt strictly
   * after BOTH the caller's record and the stored one: an undo legitimately
   * passes an older before-record, and server LWW is strictly-newer — an
   * equal-or-older stamp would lose there and the pull echo would resurrect
   * the overwritten state. Bumps page rev (thumbnails render text) + journals.
   *
   * NOTE: restamps `box.updatedAt` IN PLACE. The engine relies on this —
   * createTextBoxAt returns the box and snapshots it into the undo stack
   * AFTER this call, expecting the stored stamp (see engine.ts "stamps
   * box.updatedAt in place"). Callers that need a pristine record must pass
   * a clone. Thanks to the stored+1 stamp above this is a convenience, not a
   * correctness requirement: even a stale snapshot written back later (undo)
   * still wins LWW.
   */
  async putTextBox(box: TextBox): Promise<void> {
    const now = Date.now();
    const tx = this.db.transaction(['textboxes', 'pages', 'outbox'], 'readwrite');
    const store = tx.objectStore('textboxes');
    const prior = await store.get(box.id);
    box.updatedAt = Math.max(now, box.updatedAt + 1, (prior?.updatedAt ?? 0) + 1);
    await store.put(box);
    const outbox = tx.objectStore('outbox');
    await outbox.add({ mutation: textboxMutation(box), enqueuedAt: now });
    const page = await tx.objectStore('pages').get(box.pageId);
    if (page) {
      page.rev += 1;
      bumpUpdatedAt(page);
      await tx.objectStore('pages').put(page);
      await outbox.add({ mutation: pageMutation(page), enqueuedAt: now });
    }
    await tx.done;
    this.notifyMutation();
  }

  async getTextBox(id: string): Promise<TextBox | undefined> {
    return this.db.get('textboxes', id);
  }

  async getActiveTextBoxes(pageId: string): Promise<TextBox[]> {
    const all = await this.db.getAllFromIndex('textboxes', 'byPage', pageId);
    return all
      .filter((b) => !b.deletedAt)
      .sort((a, b) => (a.id < b.id ? -1 : 1)); // ulid = creation order = z-order
  }

  /**
   * Tombstone (ts) or restore (null) a text box. Whole-record LWW like pages:
   * the deletion state rides on updatedAt, which is stamped strictly after the
   * previous value even within one millisecond. Bumps page rev + journals.
   */
  async setTextBoxDeleted(id: string, deletedAt: number | null): Promise<void> {
    const now = Date.now();
    const tx = this.db.transaction(['textboxes', 'pages', 'outbox'], 'readwrite');
    const store = tx.objectStore('textboxes');
    const box = await store.get(id);
    if (!box) { await tx.done; return; }
    if (deletedAt === null) delete box.deletedAt; else box.deletedAt = deletedAt;
    box.updatedAt = Math.max(now, box.updatedAt + 1);
    await store.put(box);
    const outbox = tx.objectStore('outbox');
    await outbox.add({ mutation: textboxMutation(box), enqueuedAt: now });
    const page = await tx.objectStore('pages').get(box.pageId);
    if (page) {
      page.rev += 1;
      bumpUpdatedAt(page);
      await tx.objectStore('pages').put(page);
      await outbox.add({ mutation: pageMutation(page), enqueuedAt: now });
    }
    await tx.done;
    this.notifyMutation();
  }

  // ---- images (M4 spec §3) ---------------------------------------------------
  // Mirror the textbox methods EXACTLY: same restamping, journaling, page-rev
  // and LWW rules — images are textbox-shaped on the wire (kind 'image').

  /**
   * Upsert an image (insert, move, scale). Stamps updatedAt strictly after
   * BOTH the caller's record and the stored one — an undo legitimately passes
   * an older before-record, and server LWW is strictly-newer (see putTextBox).
   * Restamps `img.updatedAt` IN PLACE (undo-snapshot contract, same as
   * putTextBox). Bumps page rev + journals image and page in one transaction.
   */
  async putImage(img: Image): Promise<void> {
    const now = Date.now();
    const tx = this.db.transaction(['images', 'pages', 'outbox'], 'readwrite');
    const store = tx.objectStore('images');
    const prior = await store.get(img.id);
    img.updatedAt = Math.max(now, img.updatedAt + 1, (prior?.updatedAt ?? 0) + 1);
    await store.put(img);
    const outbox = tx.objectStore('outbox');
    await outbox.add({ mutation: imageMutation(img), enqueuedAt: now });
    const page = await tx.objectStore('pages').get(img.pageId);
    if (page) {
      page.rev += 1;
      bumpUpdatedAt(page);
      await tx.objectStore('pages').put(page);
      await outbox.add({ mutation: pageMutation(page), enqueuedAt: now });
    }
    await tx.done;
    this.notifyMutation();
  }

  async getImage(id: string): Promise<Image | undefined> {
    return this.db.get('images', id);
  }

  async getActiveImages(pageId: string): Promise<Image[]> {
    const all = await this.db.getAllFromIndex('images', 'byPage', pageId);
    return all
      .filter((i) => !i.deletedAt)
      .sort((a, b) => (a.id < b.id ? -1 : 1)); // ulid = creation order = z-order
  }

  /**
   * Tombstone (ts) or restore (null) an image. Whole-record LWW like
   * textboxes: the deletion state rides on updatedAt, stamped strictly after
   * the previous value even within one millisecond. Bumps page rev + journals.
   */
  async setImageDeleted(id: string, deletedAt: number | null): Promise<void> {
    const now = Date.now();
    const tx = this.db.transaction(['images', 'pages', 'outbox'], 'readwrite');
    const store = tx.objectStore('images');
    const img = await store.get(id);
    if (!img) { await tx.done; return; }
    if (deletedAt === null) delete img.deletedAt; else img.deletedAt = deletedAt;
    img.updatedAt = Math.max(now, img.updatedAt + 1);
    await store.put(img);
    const outbox = tx.objectStore('outbox');
    await outbox.add({ mutation: imageMutation(img), enqueuedAt: now });
    const page = await tx.objectStore('pages').get(img.pageId);
    if (page) {
      page.rev += 1;
      bumpUpdatedAt(page);
      await tx.objectStore('pages').put(page);
      await outbox.add({ mutation: pageMutation(page), enqueuedAt: now });
    }
    await tx.done;
    this.notifyMutation();
  }

  /** LWW: skip when the local row is strictly newer (page-style, whole record). */
  async applyRemoteImage(record: RemoteImage): Promise<void> {
    const tx = this.db.transaction('images', 'readwrite');
    const local = await tx.store.get(record.id);
    if (local && local.updatedAt > record.updatedAt) { await tx.done; return; }
    await tx.store.put(normalizeDeletedAt(record));
    await tx.done;
  }

  // ---- file cache (M4 spec §1) -----------------------------------------------

  /**
   * Cached read of an immutable R2 object: IDB hit → bytes; miss → `fetcher`
   * (typically `(id) => syncEngine.fetchFile(id)`) → cache → bytes.
   * Single-flight per fileId: concurrent misses share ONE fetcher invocation.
   * Failed fetches are not cached, so a later call retries.
   *
   * When sync is disabled there is no remote to fetch from — local-only flows
   * (PDF import / image insert on this device) must putFile at upload time so
   * the local copy never needs fetching (plan Task 2/5; the fetcher only runs
   * for files first seen on another device).
   */
  getFile(fileId: string, fetcher: (id: string) => Promise<ArrayBuffer>): Promise<ArrayBuffer> {
    const inflight = this.fileFetches.get(fileId);
    if (inflight) return inflight;
    const p = (async () => {
      const hit = await this.db.get('files', fileId);
      if (hit) return hit.bytes;
      // 'local-' ids only exist on the device that imported them with sync
      // disabled — the server never has them; skip the guaranteed-404 fetch.
      if (fileId.startsWith('local-')) {
        throw new Error('file was imported on another device before sync was enabled');
      }
      const bytes = await fetcher(fileId);
      await this.putFile(fileId, bytes);
      return bytes;
    })().finally(() => this.fileFetches.delete(fileId));
    this.fileFetches.set(fileId, p);
    return p;
  }

  /**
   * Store file bytes, then enforce the byte budget by evicting
   * oldest-cachedAt-first (files are immutable, so eviction only costs a
   * refetch). The just-written file is never evicted — even a single
   * over-budget file must survive its own put or getFile could never return.
   */
  async putFile(fileId: string, bytes: ArrayBuffer): Promise<void> {
    const tx = this.db.transaction('files', 'readwrite');
    await tx.store.put({ bytes, size: bytes.byteLength, cachedAt: Date.now() }, fileId);
    const entries: { key: string; size: number; cachedAt: number }[] = [];
    let total = 0;
    let cursor = await tx.store.openCursor();
    while (cursor) {
      entries.push({ key: cursor.key, size: cursor.value.size, cachedAt: cursor.value.cachedAt });
      total += cursor.value.size;
      cursor = await cursor.continue();
    }
    if (total > this.fileCacheBytes) {
      entries.sort((a, b) => a.cachedAt - b.cachedAt);
      for (const e of entries) {
        if (total <= this.fileCacheBytes) break;
        if (e.key === fileId) continue; // never evict the file just written
        // local-only files (sync-disabled imports/inserts) have NO server
        // copy — evicting one would permanently destroy its PDF/image.
        if (e.key.startsWith('local-')) continue;
        await tx.store.delete(e.key);
        total -= e.size;
      }
    }
    await tx.done;
  }

  // ---- purge (M3b spec §2: Delete forever) ----------------------------------

  /** Hard-delete a notebook cascade locally and journal a synced purge mutation. */
  async purgeNotebook(id: string): Promise<void> {
    await this.purgeLocal('notebook', id, true);
  }

  /** Hard-delete a page cascade locally and journal a synced purge mutation. */
  async purgePage(id: string): Promise<void> {
    await this.purgeLocal('page', id, true);
  }

  /**
   * Apply a purge learned from the changes feed: hard-delete the local cascade
   * and scrub the outbox, but journal NOTHING (the server already has the
   * purge, and it silently drops post-purge mutations anyway). Returns the
   * locally-affected page ids so the sync engine can emit pagechanged.
   */
  async applyRemotePurge(purge: RemotePurge): Promise<string[]> {
    return this.purgeLocal(purge.target, purge.id, false);
  }

  /**
   * Shared purge cascade: hard-delete rows (notebook → its pages → their
   * strokes+textboxes+images; page → its strokes+textboxes+images) and drop
   * every pending outbox entry targeting a purged id — the server would
   * silently drop those mutations (purge wins, no zombie resurrection), so
   * pushing them is at best wasted and at worst confusing. Queued purge
   * mutations are never dropped (re-purge is an idempotent server no-op).
   * Cached files are NOT deleted (they may be shared; LRU handles cleanup).
   */
  private async purgeLocal(target: 'notebook' | 'page', id: string, journal: boolean): Promise<string[]> {
    const now = Date.now();
    const tx = this.db.transaction(['notebooks', 'pages', 'strokes', 'textboxes', 'images', 'outbox'], 'readwrite');
    const pages = tx.objectStore('pages');
    const pageIds: string[] =
      target === 'page' ? [id] : await pages.index('byNotebook').getAllKeys(id);
    const pageIdSet = new Set(pageIds);

    const strokes = tx.objectStore('strokes');
    const textboxes = tx.objectStore('textboxes');
    const images = tx.objectStore('images');
    for (const pid of pageIds) {
      for (const key of await strokes.index('byPage').getAllKeys(pid)) await strokes.delete(key);
      for (const key of await textboxes.index('byPage').getAllKeys(pid)) await textboxes.delete(key);
      for (const key of await images.index('byPage').getAllKeys(pid)) await images.delete(key);
      await pages.delete(pid);
    }
    if (target === 'notebook') await tx.objectStore('notebooks').delete(id);

    // Outbox scrub runs BEFORE journaling our own purge so it can't eat it.
    const outbox = tx.objectStore('outbox');
    let cursor = await outbox.openCursor();
    while (cursor) {
      const m = cursor.value.mutation;
      let drop = false;
      if (m.kind === 'notebook') {
        drop = target === 'notebook' && m.id === id;
      } else if (m.kind === 'page') {
        drop =
          pageIdSet.has(m.id) ||
          (target === 'notebook' && (m.data as { notebookId?: string } | null)?.notebookId === id);
      } else if (m.kind === 'stroke' || m.kind === 'textbox' || m.kind === 'image') {
        drop = pageIdSet.has(m.pageId);
      }
      if (drop) await cursor.delete();
      cursor = await cursor.continue();
    }

    if (journal) {
      await outbox.add({ mutation: { kind: 'purge', target, id, at: now }, enqueuedAt: now });
    }
    await tx.done;
    if (journal) this.notifyMutation();
    return pageIds;
  }

  /**
   * Trash view contents (spec §2): tombstoned notebooks, and tombstoned pages
   * of LIVE notebooks only (pages of a trashed notebook restore with it),
   * newest deletion first.
   */
  async listTrash(): Promise<{ notebooks: Notebook[]; pages: (Page & { notebookTitle: string })[] }> {
    const allNotebooks = await this.db.getAll('notebooks');
    const live = new Map(allNotebooks.filter((n) => !n.deletedAt).map((n) => [n.id, n]));
    const notebooks = allNotebooks
      .filter((n) => n.deletedAt)
      .sort((a, b) => b.deletedAt! - a.deletedAt!);
    const pages = (await this.db.getAll('pages'))
      .filter((p) => p.deletedAt && live.has(p.notebookId))
      .sort((a, b) => b.deletedAt! - a.deletedAt!)
      .map((p) => ({ ...p, notebookTitle: live.get(p.notebookId)!.title }));
    return { notebooks, pages };
  }

  // ---- remote apply (sync pull) --------------------------------------------
  // These bypass the outbox (a pulled row must not be re-pushed) and never
  // re-bump page revs (remote page rows carry their own rev).

  /** LWW: skip when the local row is strictly newer than the remote one. */
  async applyRemoteNotebook(record: RemoteNotebook): Promise<void> {
    const tx = this.db.transaction('notebooks', 'readwrite');
    const local = await tx.store.get(record.id);
    if (local && local.updatedAt > record.updatedAt) { await tx.done; return; }
    await tx.store.put(normalizeDeletedAt(record));
    await tx.done;
  }

  /**
   * LWW: skip when the local row is strictly newer. Stores the remote rev
   * except that rev never goes backwards: an equal-updatedAt echo of a stale
   * server row must not regress the local rev (thumbnails key on it).
   */
  async applyRemotePage(record: RemotePage): Promise<void> {
    const tx = this.db.transaction('pages', 'readwrite');
    const local = await tx.store.get(record.id);
    if (local && local.updatedAt > record.updatedAt) { await tx.done; return; }
    const merged = normalizeDeletedAt(record);
    merged.rev = Math.max(local?.rev ?? 0, record.rev);
    await tx.store.put(merged);
    await tx.done;
  }

  /** LWW: skip when the local row is strictly newer (page-style, whole record). */
  async applyRemoteTextBox(record: RemoteTextBox): Promise<void> {
    const tx = this.db.transaction('textboxes', 'readwrite');
    const local = await tx.store.get(record.id);
    if (local && local.updatedAt > record.updatedAt) { await tx.done; return; }
    await tx.store.put(normalizeDeletedAt(record));
    await tx.done;
  }

  /**
   * Insert-if-absent (points/meta immutable once stored) + deletedAt LWW:
   * a newer remote tombstone wins; an older one never regresses a newer local
   * tombstone; a remote clear always applies (the server already resolved the
   * clear-vs-tombstone race — its feed is the source of truth).
   */
  async applyRemoteStroke(record: RemoteStroke): Promise<void> {
    const tx = this.db.transaction('strokes', 'readwrite');
    const local = await tx.store.get(record.id);
    if (!local) {
      await tx.store.put(normalizeDeletedAt(record));
      await tx.done;
      return;
    }
    const remoteDel = record.deletedAt ?? null;
    if (remoteDel !== null) {
      if (local.deletedAt === undefined || local.deletedAt < remoteDel) {
        local.deletedAt = remoteDel;
        await tx.store.put(local);
      }
    } else if (local.deletedAt !== undefined) {
      delete local.deletedAt;
      await tx.store.put(local);
    }
    await tx.done;
  }

  // ---- sync outbox / meta ---------------------------------------------------

  /** Oldest-first batch of pending mutations (enqueue order = key order). */
  async getOutboxBatch(limit: number): Promise<{ key: number; mutation: Mutation }[]> {
    const out: { key: number; mutation: Mutation }[] = [];
    let cursor = await this.db.transaction('outbox').store.openCursor();
    while (cursor && out.length < limit) {
      out.push({ key: cursor.key, mutation: cursor.value.mutation });
      cursor = await cursor.continue();
    }
    return out;
  }

  /** Remove pushed outbox rows (call only after the server 2xx'd them). */
  async deleteOutbox(keys: number[]): Promise<void> {
    const tx = this.db.transaction('outbox', 'readwrite');
    for (const k of keys) await tx.store.delete(k);
    await tx.done;
  }

  async getSyncMeta(key: string): Promise<unknown> {
    return this.db.get('syncmeta', key);
  }

  async setSyncMeta(key: string, value: unknown): Promise<void> {
    await this.db.put('syncmeta', value, key);
  }

  /**
   * First-enable export (spec §3.4): journal every existing record —
   * live and tombstoned — in dependency order (notebooks, pages, textboxes,
   * images, strokes), all in one transaction. Stroke inserts carry no
   * updatedAt so an export can never clear a tombstone another device set
   * (see strokeInsertMutation). Textbox/image exports reuse the stored
   * updatedAt, so the server's strictly-newer LWW makes a re-export a
   * harmless no-op there.
   */
  async enqueueFullExport(): Promise<void> {
    const tx = this.db.transaction(['notebooks', 'pages', 'textboxes', 'images', 'strokes', 'outbox'], 'readwrite');
    const outbox = tx.objectStore('outbox');
    const enqueuedAt = Date.now();
    for (const nb of await tx.objectStore('notebooks').getAll()) {
      await outbox.add({ mutation: notebookMutation(nb), enqueuedAt });
    }
    for (const p of await tx.objectStore('pages').getAll()) {
      await outbox.add({ mutation: pageMutation(p), enqueuedAt });
    }
    for (const b of await tx.objectStore('textboxes').getAll()) {
      await outbox.add({ mutation: textboxMutation(b), enqueuedAt });
    }
    for (const img of await tx.objectStore('images').getAll()) {
      await outbox.add({ mutation: imageMutation(img), enqueuedAt });
    }
    for (const s of await tx.objectStore('strokes').getAll()) {
      await outbox.add({ mutation: strokeInsertMutation(s), enqueuedAt });
    }
    await tx.done;
    this.notifyMutation();
  }

  // ---- settings ----
  async getSettings(): Promise<Settings> {
    const s = (await this.db.get('meta', 'settings')) as Partial<Settings> | undefined;
    return { ...DEFAULT_SETTINGS, singleFingerDraws: defaultSingleFingerDraws(), ...s };
  }

  async patchSettings(patch: Partial<Settings>): Promise<void> {
    // Read+merge+write inside one readwrite tx so concurrent patches
    // serialize instead of overwriting each other (lost update).
    // The platform-aware default is applied here too, so a patch of some other
    // key doesn't bake a hardcoded `false` over the platform default.
    const tx = this.db.transaction('meta', 'readwrite');
    const current = ((await tx.store.get('settings')) ?? {}) as Partial<Settings>;
    await tx.store.put({ ...DEFAULT_SETTINGS, singleFingerDraws: defaultSingleFingerDraws(), ...current, ...patch }, 'settings');
    await tx.done;
  }
}

/** Convert a server row (deletedAt: null) to the local convention (field absent). */
function normalizeDeletedAt<T extends { deletedAt?: number | null }>(record: T): T & { deletedAt?: number } {
  const copy = { ...record };
  if (copy.deletedAt == null) delete copy.deletedAt;
  return copy as T & { deletedAt?: number };
}

function f32ToBuffer(f: Float32Array): ArrayBuffer {
  // slice to a tightly-sized copy so we never persist a growable builder's backing buffer
  return f.buffer.slice(f.byteOffset, f.byteOffset + f.byteLength) as ArrayBuffer;
}
